import styled from "styled-components";
import { CSSVars } from "@/styles/styles.config";

export const StyledDemo = styled.div`
  --bot-accent: ${CSSVars.blue600};

  width: 100%;
  max-width: 600px;
  margin: 40px auto;
  text-align: left;

  border: 1px solid ${CSSVars.gray200};
  border-radius: 18px;
  background: ${CSSVars.white};
  box-shadow:
    0 1px 2px rgba(16, 17, 40, 0.04),
    0 24px 60px -32px rgba(16, 17, 40, 0.35);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .demo-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 11px 14px;
    background: ${CSSVars.gray50};
    border-bottom: 1px solid ${CSSVars.gray200};
    font-family: ${CSSVars.secondaryFont};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: ${CSSVars.gray600};
  }

  .demo-label .live {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: ${CSSVars.gray500};
    text-transform: none;
    letter-spacing: 0;
    font-weight: 400;
    font-size: 12px;
  }

  .demo-label .pulse {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #16a06a;
    box-shadow: 0 0 0 0 rgba(22, 160, 106, 0.5);
    animation: cnPulse 2.2s ease-out infinite;
  }

  @keyframes cnPulse {
    0% {
      box-shadow: 0 0 0 0 rgba(22, 160, 106, 0.45);
    }
    70% {
      box-shadow: 0 0 0 7px rgba(22, 160, 106, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(22, 160, 106, 0);
    }
  }

  /* ---------- bot switcher ---------- */
  .tabs {
    display: flex;
    gap: 6px;
    padding: 10px 12px;
    overflow-x: auto;
    border-bottom: 1px solid ${CSSVars.gray200};
    scrollbar-width: none;
  }
  .tabs::-webkit-scrollbar {
    display: none;
  }

  .tab {
    flex: 0 0 auto;
    font-family: ${CSSVars.secondaryFont};
    font-size: 13px;
    font-weight: 600;
    color: ${CSSVars.gray600};
    background: transparent;
    border: 1px solid ${CSSVars.gray200};
    border-radius: 100px;
    padding: 7px 14px;
    cursor: pointer;
    white-space: nowrap;
    transition:
      background 0.15s ease,
      color 0.15s ease,
      border-color 0.15s ease;
  }

  .tab:hover {
    border-color: ${CSSVars.gray400};
    color: ${CSSVars.black};
  }

  .tab[aria-selected="true"] {
    background: var(--bot-accent);
    border-color: var(--bot-accent);
    color: ${CSSVars.white};
  }

  .tab:focus-visible {
    outline: 2px solid ${CSSVars.blue500};
    outline-offset: 2px;
  }

  /* ---------- chat header ---------- */
  .bot-head {
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 14px 16px;
    border-bottom: 1px solid ${CSSVars.gray100};
  }

  .avatar {
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    border-radius: 10px;
    background: var(--bot-accent);
    color: ${CSSVars.white};
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: ${CSSVars.primaryFont};
    font-weight: 600;
    font-size: 13px;
    letter-spacing: 0.02em;
  }

  .bot-head .name {
    font-family: ${CSSVars.primaryFont};
    font-weight: 600;
    font-size: 15px;
    color: ${CSSVars.black};
    display: block;
    line-height: 1.3;
  }

  .bot-head .knowledge {
    font-family: ${CSSVars.secondaryFont};
    font-size: 12px;
    color: ${CSSVars.gray500};
    display: block;
    line-height: 1.4;
  }

  /* ---------- transcript ---------- */
  .thread {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 300px;
    max-height: 340px;
    overflow-y: auto;
    background:
      radial-gradient(
        circle at 1px 1px,
        ${CSSVars.gray100} 1px,
        transparent 0
      )
      0 0 / 16px 16px;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      min-height: 336px;
      max-height: 380px;
    }
  }

  .msg {
    max-width: 86%;
    font-family: ${CSSVars.secondaryFont};
    font-size: 14.5px;
    line-height: 1.55;
    padding: 11px 14px;
    border-radius: 14px;
    animation: cnRise 0.25s ease-out both;
  }

  @keyframes cnRise {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .msg {
      animation: none;
    }
    .pulse {
      animation: none;
    }
  }

  .msg.bot {
    align-self: flex-start;
    background: ${CSSVars.white};
    border: 1px solid ${CSSVars.gray200};
    color: ${CSSVars.gray900};
    border-bottom-left-radius: 5px;
  }

  .msg.user {
    align-self: flex-end;
    background: var(--bot-accent);
    color: ${CSSVars.white};
    border-bottom-right-radius: 5px;
  }

  .caret {
    display: inline-block;
    width: 2px;
    height: 1em;
    background: currentColor;
    vertical-align: -2px;
    margin-left: 2px;
    animation: cnBlink 1s steps(2, start) infinite;
  }

  @keyframes cnBlink {
    to {
      visibility: hidden;
    }
  }

  .typing {
    align-self: flex-start;
    display: inline-flex;
    gap: 4px;
    padding: 13px 14px;
    background: ${CSSVars.white};
    border: 1px solid ${CSSVars.gray200};
    border-radius: 14px;
    border-bottom-left-radius: 5px;
  }

  .typing span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${CSSVars.gray400};
    animation: cnDot 1.1s ease-in-out infinite;
  }
  .typing span:nth-child(2) {
    animation-delay: 0.16s;
  }
  .typing span:nth-child(3) {
    animation-delay: 0.32s;
  }

  @keyframes cnDot {
    0%,
    60%,
    100% {
      opacity: 0.35;
      transform: translateY(0);
    }
    30% {
      opacity: 1;
      transform: translateY(-3px);
    }
  }

  /* ---------- product cards ---------- */
  .cards {
    align-self: flex-start;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    max-width: 100%;
  }

  .pcard {
    display: flex;
    align-items: center;
    gap: 10px;
    border: 1px solid ${CSSVars.gray200};
    background: ${CSSVars.white};
    border-radius: 12px;
    padding: 9px 12px 9px 9px;
    animation: cnRise 0.25s ease-out both;
  }

  .pcard .thumb {
    width: 44px;
    height: 44px;
    flex: 0 0 44px;
    border-radius: 8px;
    background: ${CSSVars.gray100};
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--bot-accent);
  }

  .pcard .title {
    font-family: ${CSSVars.primaryFont};
    font-weight: 600;
    font-size: 13.5px;
    color: ${CSSVars.black};
    display: block;
    line-height: 1.3;
  }

  .pcard .meta {
    font-family: ${CSSVars.secondaryFont};
    font-size: 12px;
    color: ${CSSVars.gray500};
    display: block;
  }

  .pcard .price {
    font-family: ${CSSVars.primaryFont};
    font-weight: 600;
    font-size: 14px;
    color: var(--bot-accent);
    margin-left: 4px;
  }

  .inline-action {
    align-self: flex-start;
    font-family: ${CSSVars.secondaryFont};
    font-size: 13.5px;
    font-weight: 600;
    color: ${CSSVars.white};
    background: var(--bot-accent);
    border: 0;
    border-radius: 10px;
    padding: 9px 15px;
    cursor: default;
    animation: cnRise 0.25s ease-out both;
  }

  /* ---------- prompts + composer ---------- */
  .prompts {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    padding: 12px 16px 0;
  }

  .prompt {
    font-family: ${CSSVars.secondaryFont};
    font-size: 13px;
    font-weight: 600;
    color: var(--bot-accent);
    background: ${CSSVars.white};
    border: 1px solid ${CSSVars.gray200};
    border-radius: 100px;
    padding: 7px 13px;
    cursor: pointer;
    transition:
      border-color 0.15s ease,
      transform 0.15s ease;
  }

  .prompt:hover:not(:disabled) {
    border-color: var(--bot-accent);
    transform: translateY(-1px);
  }

  .prompt:disabled {
    opacity: 0.45;
    cursor: default;
  }

  .prompt:focus-visible {
    outline: 2px solid ${CSSVars.blue500};
    outline-offset: 2px;
  }

  .composer {
    display: flex;
    gap: 8px;
    padding: 12px 16px 16px;
  }

  .composer input {
    flex: 1;
    min-width: 0;
    font-family: ${CSSVars.secondaryFont};
    font-size: 14px;
    color: ${CSSVars.black};
    border: 1px solid ${CSSVars.gray200};
    border-radius: 10px;
    padding: 10px 13px;
    background: ${CSSVars.white};
  }

  .composer input:focus {
    outline: none;
    border-color: var(--bot-accent);
  }

  .composer button {
    flex: 0 0 auto;
    border: 0;
    border-radius: 10px;
    background: var(--bot-accent);
    color: ${CSSVars.white};
    font-family: ${CSSVars.secondaryFont};
    font-weight: 600;
    font-size: 14px;
    padding: 0 16px;
    cursor: pointer;
  }

  .composer button:disabled {
    opacity: 0.45;
    cursor: default;
  }

  /* ---------- conversion strip ---------- */
  .convert {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px 14px;
    padding: 13px 16px;
    border-top: 1px solid ${CSSVars.gray200};
    background: ${CSSVars.blue50};
  }

  .convert p {
    font-family: ${CSSVars.secondaryFont};
    font-size: 13.5px;
    color: ${CSSVars.gray700};
    margin: 0;
  }

  /* The button itself is the shared gradient CustomLink, so it matches every
     other hub. Only its auto margins are reset to sit in this row. */
  .convert a {
    margin: 0;
  }
`;
